import { useEffect, useState } from "react";
import {
	Box, Button, Card, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Axios from "services/api/config";
import {
	getAllTicketsURL, getTicketURL, getTicketContextURL, replyTicketURL, updateTicketStatusURL, trackShipmentURL, trackReturnURL,
} from "services/api/routes/Support";

const STATUSES = ["OPEN", "IN_PROGRESS", "WAITING", "RESOLVED", "CLOSED"];
const CATEGORIES = ["", "ORDER", "RETURN", "REFUND", "PAYMENT", "PRODUCT", "PLATFORM", "OTHER"];

const pretty = (v: string) => (v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, " ") : "");
const DOT: Record<string, string> = { OPEN: "#2563EB", IN_PROGRESS: "#7C3AED", WAITING: "#D97706", RESOLVED: "#16A34A", CLOSED: "#6B7280" };

// A real dropdown (label, arrow, border). The dashboard theme hides the select arrow
// and squashes its padding globally, which made these look like plain text boxes.
const FilterSelect = ({ label, value, onChange, options, allLabel }: {
	label: string; value: string; onChange: (v: string) => void; options: string[]; allLabel: string;
}) => (
	<FormControl size="small" sx={{ minWidth: 200 }}>
		<InputLabel shrink id={`${label}-label`}>{label}</InputLabel>
		<Select
			labelId={`${label}-label`}
			label={label}
			value={value}
			displayEmpty
			notched
			onChange={(e) => onChange(String(e.target.value))}
			IconComponent={KeyboardArrowDownRoundedIcon}
			sx={{
				bgcolor: "#fff",
				borderRadius: 2,
				"& .MuiSelect-select": { display: "flex !important", alignItems: "center", gap: 1, py: "8.5px !important", pl: "14px !important", pr: "36px !important", minHeight: "unset" },
				"& .MuiSelect-icon": { display: "block !important", color: "#6B7280", right: 8 },
			}}
			renderValue={(v: any) => (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
					{v && DOT[v] && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: DOT[v] }} />}
					{v ? pretty(v) : allLabel}
				</Box>
			)}
		>
			<MenuItem value="">{allLabel}</MenuItem>
			{options.filter(Boolean).map((o) => (
				<MenuItem key={o} value={o} sx={{ display: "flex", gap: 1 }}>
					{DOT[o] && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: DOT[o] }} />}
					{pretty(o)}
				</MenuItem>
			))}
		</Select>
	</FormControl>
);

const statusColor = (s: string): any =>
	s === "RESOLVED" || s === "CLOSED" ? "success" : s === "WAITING" ? "warning" : "info";

const roleLabel = (r: string): string =>
	r === "ROLE_CUSTOMER" ? "Customer" : r === "ROLE_MERCHANT" ? "Merchant" : r === "ROLE_ADMIN" ? "Admin" : r === "ROLE_SUPPORT" ? "Help desk" : r || "—";

const Field = ({ label, value }: { label: string; value: any }) =>
	value === null || value === undefined || value === "" ? null : (
		<Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
			<Typography className="!text-sm !text-gray-500" sx={{ minWidth: 92 }}>{label}</Typography>
			<Typography className="!text-sm !font-medium" sx={{ wordBreak: "break-all" }}>{String(value)}</Typography>
		</Box>
	);

// Resolved order / return context for the ticket (replaces raw IDs).
const TicketContext = ({ ctx }: { ctx: any }) => {
	if (!ctx) return null;
	const money = (v: any) => (v === null || v === undefined ? "" : `₹${v}`);
	return (
		<Box sx={{ border: "1px solid #ECECEC", borderRadius: 2, p: 2, mb: 3, bgcolor: "#FFFDF8" }}>
			<Typography className="!text-sm !font-semibold !mb-2">Order details</Typography>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
				<Field label="Order ID" value={ctx.orderId} />
				<Field label="Status" value={ctx.orderStatus} />
				<Field label="Sub-order" value={ctx.subOrderId} />
				<Field label="Order total" value={money(ctx.totalPrice)} />
				<Field label="Customer" value={ctx.customerName} />
				<Field label="Cust. email" value={ctx.customerEmail} />
				<Field label="Cust. phone" value={ctx.customerPhone} />
				<Field label="Deliver to" value={ctx.deliveryAddress} />
				<Field label="Courier" value={ctx.courierName} />
				<Field label="Tracking no." value={ctx.trackingNumber} />
				<Field label="Return status" value={ctx.returnStatus} />
				<Field label="Return courier" value={ctx.returnCourierName} />
				<Field label="Return tracking" value={ctx.returnTrackingNumber} />
				<Field label="Merchant" value={ctx.merchantBrand} />
				<Field label="Merchant ID" value={ctx.merchantId} />
				<Field label="Return ID" value={ctx.returnItemQueueId} />
				<Field label="Return qty" value={ctx.returnQuantity} />
				<Field label="Return reason" value={ctx.returnReason} />
			</Box>
			{Array.isArray(ctx.items) && ctx.items.length > 0 && (
				<Box sx={{ mt: 2 }}>
					<Typography className="!text-sm !font-semibold !mb-1">Products</Typography>
					{ctx.items.map((it: any, i: number) => (
						<Box key={i} sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", py: 0.5, borderTop: i ? "1px dashed #eee" : "none" }}>
							<Typography className="!text-sm !font-medium">{it.productName || "Product"}</Typography>
							{it.size && <Chip size="small" variant="outlined" label={`Size ${it.size}`} />}
							{it.quantity != null && <Chip size="small" variant="outlined" label={`Qty ${it.quantity}`} />}
							{it.unitPrice != null && <Chip size="small" variant="outlined" label={`₹${it.unitPrice}`} />}
							{it.productId && <Typography className="!text-xs !text-gray-400" sx={{ wordBreak: "break-all" }}>#{it.productId}</Typography>}
						</Box>
					))}
				</Box>
			)}
			<CourierTimeline ctx={ctx} />
		</Box>
	);
};

// The courier's scan history for the parcel (or the return pickup) — what support
// needs to answer "where is my order?" without opening Shiprocket.
const CourierTimeline = ({ ctx }: { ctx: any }) => {
	const [events, setEvents] = useState<any[] | null>(null);
	const [current, setCurrent] = useState<string>("");
	const [error, setError] = useState("");
	const hasParcel = !!(ctx?.trackingNumber || ctx?.returnTrackingNumber);
	const target = ctx?.returnItemQueueId ? trackReturnURL(ctx.returnItemQueueId) : ctx?.subOrderId ? trackShipmentURL(ctx.subOrderId) : null;
	useEffect(() => {
		setEvents(null);
		setError("");
		if (!target || !hasParcel) return;
		Axios({ ...target })
			.then(({ data }) => {
				const td = data?.data?.tracking_data || {};
				setCurrent(td?.shipment_track?.[0]?.current_status || "");
				setEvents(td?.shipment_track_activities || []);
			})
			.catch(() => setError("Courier timeline isn't available right now."));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ctx?.subOrderId, ctx?.returnItemQueueId]);
	if (!target || !hasParcel) return null;
	return (
		<Box sx={{ mt: 2 }}>
			<Typography className="!text-sm !font-semibold !mb-1">
				Courier timeline{ctx?.returnItemQueueId ? " (return pickup)" : ""}{current ? ` — ${current}` : ""}
			</Typography>
			{error ? (
				<Typography className="!text-sm !text-gray-500">{error}</Typography>
			) : events === null ? (
				<CircularProgress size={18} />
			) : events.length === 0 ? (
				<Typography className="!text-sm !text-gray-500">No courier updates yet.</Typography>
			) : (
				events.slice(0, 12).map((e: any, i: number) => (
					<Box key={i} sx={{ display: "flex", gap: 1.5, py: 0.5, borderTop: i ? "1px dashed #eee" : "none" }}>
						<Typography className="!text-xs !text-gray-500" sx={{ minWidth: 130 }}>{e.date}</Typography>
						<Typography className="!text-sm">{e.activity || e["sr-status-label"]}{e.location ? ` · ${e.location}` : ""}</Typography>
					</Box>
				))
			)}
		</Box>
	);
};

function HelpAndSupport() {
	const [tickets, setTickets] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [statusFilter, setStatusFilter] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [active, setActive] = useState<any | null>(null);
	const [ctx, setCtx] = useState<any | null>(null);
	const [reply, setReply] = useState("");
	const [busy, setBusy] = useState(false);

	const load = async () => {
		setLoading(true);
		try {
			const { data } = await Axios({
				...getAllTicketsURL({ status: statusFilter || undefined, category: categoryFilter || undefined, page: 0, size: 50 }),
			});
			setTickets(data?.data?.data || []);
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statusFilter, categoryFilter]);

	const openThread = async (id: string) => {
		setCtx(null);
		try {
			const { data } = await Axios({ ...getTicketURL(id) });
			setActive(data?.data);
			try {
				const res = await Axios({ ...getTicketContextURL(id) });
				setCtx(res?.data?.data || null);
			} catch (ctxErr) {
				console.error(ctxErr);
			}
		} catch (e) {
			console.error(e);
		}
	};

	const sendReply = async () => {
		if (!reply.trim()) return;
		setBusy(true);
		try {
			const { data } = await Axios({ ...replyTicketURL(active.id, reply.trim()) });
			setActive(data?.data);
			setReply("");
			load();
		} catch (e: any) {
			alert(e?.response?.data?.errors?.[0] || "Failed to send reply");
		} finally {
			setBusy(false);
		}
	};

	const changeStatus = async (status: string) => {
		setBusy(true);
		try {
			const { data } = await Axios({ ...updateTicketStatusURL(active.id, status) });
			setActive(data?.data);
			load();
		} catch (e: any) {
			alert(e?.response?.data?.errors?.[0] || "Failed to update status");
		} finally {
			setBusy(false);
		}
	};

	// ----- thread view -----
	if (active) {
		return (
			<Card className="my-6 p-3 md:p-6">
				<Box className="flex justify-between items-center mb-4">
					<Button onClick={() => { setActive(null); setCtx(null); }}>← Back to tickets</Button>
					<Box className="flex items-center gap-2">
						<FilterSelect label="Status" value={active.status} onChange={(v) => v && changeStatus(v)} options={STATUSES} allLabel="Set status" />
						{active.status !== "CLOSED" && (
							<Button variant="contained" color="error" size="small" disabled={busy} onClick={() => changeStatus("CLOSED")}>
								Close ticket
							</Button>
						)}
					</Box>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography className="!text-xl !font-medium">{active.subject}</Typography>
					{active.escalated && <Chip label="Escalated by seller" color="warning" size="small" />}
				</Box>
				<Typography className="!text-sm !text-gray-500 !mb-3">
					{active.ticketNumber} · {active.category} · Raised by {roleLabel(active.raisedByRole)}
				</Typography>
				<TicketContext ctx={ctx} />
				<Box sx={{ maxHeight: 440, overflowY: "auto", p: 1, bgcolor: "#FAFAFA", borderRadius: 2 }}>
					{(active.messages || []).map((m: any, i: number) => {
						const support = m.authorRole === "ROLE_ADMIN";
						return (
							<Box key={i} sx={{ display: "flex", justifyContent: support ? "flex-end" : "flex-start", mb: 1.5 }}>
								<Box sx={{ maxWidth: "70%", p: 1.5, borderRadius: 2, bgcolor: support ? "#1976d2" : "#ECECEC", color: support ? "#fff" : "#000" }}>
									<Typography className="!text-xs !font-semibold">
										{support ? "Support (you)" : m.authorRole === "ROLE_MERCHANT" ? "Merchant" : "Customer"}
									</Typography>
									<Typography className="!text-sm">{m.body}</Typography>
								</Box>
							</Box>
						);
					})}
				</Box>
				{active.status !== "CLOSED" && (
					<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
						<TextField fullWidth size="small" placeholder="Reply to the user…" value={reply} onChange={(e) => setReply(e.target.value)} />
						<Button variant="contained" disabled={busy} onClick={sendReply}>Send</Button>
					</Box>
				)}
			</Card>
		);
	}

	// ----- list view -----
	return (
		<Card className="my-6 p-3 md:p-6">
			<Typography className="!text-2xl !font-medium !mb-1">Help & Support — Tickets</Typography>
			<Typography className="!text-sm !text-gray-500 !mb-4">Customer and merchant queries.</Typography>
			<Box className="flex flex-wrap items-center gap-3 mb-5">
				<FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUSES} allLabel="All statuses" />
				<FilterSelect label="Category" value={categoryFilter} onChange={setCategoryFilter} options={CATEGORIES} allLabel="All categories" />
				{!loading && (
					<Typography className="!text-sm !text-gray-500">
						{tickets.length} ticket{tickets.length === 1 ? "" : "s"}
					</Typography>
				)}
				{(statusFilter || categoryFilter) && (
					<Button size="small" variant="text" onClick={() => { setStatusFilter(""); setCategoryFilter(""); }}>
						Clear filters
					</Button>
				)}
			</Box>
			{loading ? (
				<Box className="flex justify-center p-6"><CircularProgress /></Box>
			) : tickets.length === 0 ? (
				<Typography>No tickets found.</Typography>
			) : (
				tickets.map((t) => (
					<Card key={t.id} className="p-3 mb-2" sx={{ cursor: "pointer" }} onClick={() => openThread(t.id)}>
						<Box className="flex justify-between items-center">
							<Box>
								<Typography className="!font-medium">{t.subject}</Typography>
								<Typography className="!text-xs !text-gray-500">
									{t.ticketNumber} · {t.category} · {t.raisedByRole}
									{t.merchantId ? " · seller" : " · platform"}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{t.escalated && <Chip label="Escalated" color="warning" size="small" />}
								<Chip label={t.status} color={statusColor(t.status)} size="small" />
							</Box>
						</Box>
					</Card>
				))
			)}
		</Card>
	);
}

export default HelpAndSupport;
